// src/hooks/useFirstInstall.ts
//
// iSentinel360 v0.4.6i — first-install detection hook.
//
// v0.4.6i BUG FIX: the previous version (v0.4.6) used the
// relative URL `/api/apk-version`, which from the PWA hosted at
// app.isentinel360.com resolves to the wrong origin (it hits
// app.isentinel360.com/api/apk-version, which doesn't exist).
// The fetch always returned 404, the banner never got the
// downloadUrl, and the user never saw an "Install" button. This
// version uses the absolute URL https://api.isentinel360.com/
// api/apk-version so the request lands on the real API server.
//
// NOTE: this hook is still used by FirstInstallBanner.tsx as a
// fallback for users who have dismissed the v0.4.6i
// ChromeInstallRedirect and re-loaded the PWA. The new
// ChromeInstallRedirect is the primary install-detection
// mechanism; this hook is a backup.
import { useEffect, useState, useCallback } from 'react';

const DISMISS_KEY = 'is360:first_install_dismissed';

export type ApkVersionInfo = {
  versionCode: number;
  versionName: string;
  downloadUrl: string;
  releaseNotes?: string;
  mandatory?: boolean;
  minSupportedVersionCode?: number;
};

export type FirstInstallState = {
  loaded: boolean;
  shouldShow: boolean;
  isAndroidMobile: boolean;
  apkInfo: ApkVersionInfo | null;
  loading: boolean;
  error: string | null;
  dismiss: () => void;
  refresh: () => void;
};

function isAndroidMobileUA(ua: string): boolean {
  return /Android/i.test(ua) && /Mobile/i.test(ua);
}

function wasDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function setDismissed(): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // sessionStorage may be disabled in private/incognito — best effort
  }
}

export function useFirstInstall(): FirstInstallState {
  const [loaded, setLoaded] = useState(false);
  const [isAndroidMobile, setIsAndroidMobile] = useState(false);
  const [inApk] = useState(() => typeof window !== 'undefined' && !!(window as any).Android);
  const [dismissed] = useState(wasDismissed);
  const [apkInfo, setApkInfo] = useState<ApkVersionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // v0.4.6i BUG FIX: absolute URL. The v0.4.6 version used
      // '/api/apk-version' (relative) which from app.isentinel360.com
      // resolves to the WRONG origin. Use the API base explicitly.
      const res = await fetch(
        'https://api.isentinel360.com/api/apk-version',
        { credentials: 'omit' }
      );
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        setApkInfo(null);
        return;
      }
      const data: ApkVersionInfo = await res.json();
      setApkInfo(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setApkInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (inApk) {
      setLoaded(true);
      return;
    }
    const ua = navigator.userAgent || '';
    const androidMobile = isAndroidMobileUA(ua);
    setIsAndroidMobile(androidMobile);
    if (androidMobile && !dismissed) {
      refresh();
    }
    setLoaded(true);
  }, [inApk, dismissed, refresh]);

  const shouldShow = !inApk && isAndroidMobile && !dismissed && !!apkInfo;

  return {
    loaded,
    shouldShow,
    isAndroidMobile,
    apkInfo,
    loading,
    error,
    dismiss: setDismissed,
    refresh,
  };
}
